import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Three kinds, because two would force a lie.
 *
 * A call that nobody answered is neither a success nor a failure — it is simply
 * what happened. Styling it as an error would tell an operator something went
 * wrong every time a prospect was out, and styling it as a success is worse.
 */
export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
}

/**
 * The global toast service (S03).
 *
 * In the store rather than in React context so a mutation's error handler can
 * raise one without a component in scope — which is where most of them come from.
 */
const slice = createSlice({
  name: 'toasts',
  initialState: { items: [] as Toast[] },
  reducers: {
    pushToast: {
      reducer(state, action: PayloadAction<Toast>) {
        // Cap the stack: a failing poll should not bury the screen.
        state.items = [...state.items.slice(-2), action.payload];
      },
      prepare(kind: ToastKind, message: string) {
        return { payload: { id: crypto.randomUUID(), kind, message } };
      },
    },
    dismissToast(state, action: PayloadAction<string>) {
      state.items = state.items.filter((t) => t.id !== action.payload);
    },
  },
});

export const { pushToast, dismissToast } = slice.actions;
export const toastReducer = slice.reducer;
